import cn from 'classnames';
import { capitalize } from 'lodash';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Input, Modal, showNotification, useQueryFetch } from 'sonata-react-components';
import { checkUserResourcePermissions, getUserName } from 'sonata-static-storage/lib/helpers/user.helpers';
import { alarmsDictionary } from 'sonata-static-storage/lib/samples/alarms.samples';
import { UserRoleInterface } from 'sonata-static-storage/lib/schemas/user/userRole.schema';

import {
  getNotificationsSettingsByUser,
  setNotificationsSettingsByUser
} from '../../../services/notificationSettings.service';
import { AlarmItem } from './AlarmItem';
import { NotificationSettingsStyled } from './NotificationSettings.styles';
import { Notifications } from './types';

type Props = {
  onClose: () => void;
  user: Record<string, any>;
  userRoles: UserRoleInterface[];
};

const emptyNotifications = {
  alarms: [],
  email: ''
};

function NotificationSettings({ onClose, user, userRoles }: Props) {
  const [alarms, setAlarms] = useState<Notifications['alarms']>(emptyNotifications.alarms);
  const [email, setEmail] = useState<Notifications['email']>(emptyNotifications.email);
  const { formatMessage } = useIntl();

  const notificationSettingsByUserToken = {};
  const { refetch } = useQueryFetch(
    'notification-settings',
    async () => {
      const notificationSettings = await getNotificationsSettingsByUser(user, notificationSettingsByUserToken);
      if (notificationSettings?.alarms?.length) {
        setAlarms(notificationSettings.alarms);
      }
      return notificationSettings;
    },
    {
      enabled: false
    }
  );

  useEffect(() => {
    refetch();
    return () => {
      setAlarms(emptyNotifications.alarms);
      setEmail(emptyNotifications.email);
    };
  }, []);

  const handleAddAlarm = (newAlarm: Notifications['alarms'][0]) => {
    setAlarms([...alarms, newAlarm]);
  };

  const handleSetAlarm = (newAlarm: Notifications['alarms'][0]) => {
    const newAlarms = alarms.map(alarm => {
      if (alarm.type === newAlarm.type) {
        return newAlarm;
      }
      return alarm;
    });

    setAlarms(newAlarms);
  };

  const currentNotifications = {
    alarms,
    email
  };

  const notificationsSettingsByUserToken: Record<string, any> = {};
  const buttons = [
    {
      customClass: 'small',
      onClick: onClose,
      shape: 'squareRounded',
      text: formatMessage({
        defaultMessage: 'close',
        id: 'menu.common.close'
      }),
      type: 'secondary'
    },
    {
      customClass: 'small ',
      onClick: async () => {
        try {
          if (currentNotifications.email) {
            await setNotificationsSettingsByUser(currentNotifications, user, notificationsSettingsByUserToken);
            showNotification(
              'success',
              formatMessage({
                defaultMessage: 'User notification settings successfully saved',
                id: 'menu.notificationSettings.userNotificationSaveSuccess'
              })
            );
          } else {
            showNotification(
              'error',
              formatMessage({
                defaultMessage: 'User notification setting subscription emails field is not valid',
                id: 'menu.notificationSettings.notificationValidationErrorEmailIsValid'
              })
            );
          }
        } catch {
          showNotification(
            'error',
            formatMessage({
              defaultMessage: 'User notification settings failed',
              id: 'menu.notificationSettings.userNotificationSaveFaild'
            })
          );
        }
      },
      shape: 'squareRounded',
      text: formatMessage({
        defaultMessage: 'Save',
        id: 'menu.common.save'
      })
    }
  ];

  const getAlarmsCatalog = () => {
    if (!currentNotifications) {
      return [];
    }
    return alarmsDictionary.definitions.map(alarmDefinition => {
      const userAlarm = currentNotifications.alarms.find(a => a.type === alarmDefinition.type);
      const alarmType = alarmDefinition.type
        .split('_')
        .map(word => capitalize(word))
        .join('');
      return {
        description: formatMessage({
          defaultMessage: 'Alarms',
          id: `notificationSettings.alarms.alarmType${alarmType}Description`
        }),
        enabled: userAlarm ? userAlarm.enabled : false,
        name: formatMessage({
          defaultMessage: 'Alarms',
          id: `notificationSettings.alarms.alarmType${alarmType}`
        }),
        subscribed: userAlarm ? userAlarm.subscribed : false,
        type: alarmDefinition.type
      };
    });
  };

  const alarmsCatalog = getAlarmsCatalog();
  const notificationsMailingPermission = checkUserResourcePermissions('notificationsMailing', user, userRoles);
  const subscribedAlarmsNumber = alarmsCatalog.filter(a => a.subscribed).length;
  const enabledAlarmsNumber = alarmsCatalog.filter(a => a.enabled).length;
  const alarmsResumeClass = subscribedAlarmsNumber > 0 || enabledAlarmsNumber > 0 ? '' : 'hide';
  const includeBarClass =
    subscribedAlarmsNumber > 0 || enabledAlarmsNumber > 0 ? 'userNotifications__alarms--withoutBar' : '';

  return (
    <NotificationSettingsStyled>
      <Modal
        buttons={buttons}
        onClose={onClose}
        title={formatMessage(
          {
            defaultMessage: 'User notification settings',
            id: 'menu.notificationSettings.title'
          },
          {
            userName: getUserName(user, false)
          }
        )}
      >
        <div className="userNotifications">
          <div className={cn('userNotifications__alarmsResume', alarmsResumeClass)}>
            <div className="enabledAlarms">
              <span className="infoLabel">
                {`${formatMessage({
                  defaultMessage: 'Enabled Alarms',
                  id: 'menu.notificationSettings.enabledAlarms'
                })}:`}
              </span>
              <span className="infoNumber">{enabledAlarmsNumber}</span>
              <span className="fa fa-info-circle userNotifications__enabledAlarmsInfoToggle" />
            </div>
            <div className="subscribedAlarms">
              <span className="infoLabel">
                {`${formatMessage({
                  defaultMessage: 'Subscribed Alarms',
                  id: 'menu.notificationSettings.subscribedAlarms'
                })}:`}
              </span>
              <span className="infoNumber">{subscribedAlarmsNumber}</span>
              <span className="fa fa-info-circle userNotifications_subscribedAlarmsInfoToggle" />
            </div>
          </div>
          <div className={cn('userNotifications__alarms borderTopBox', includeBarClass)}>
            <div className="alarmsHeader userNotifications__alarmsControls">
              <div className="footNote toggle">
                {formatMessage({
                  defaultMessage: 'Enable/Disable',
                  id: 'menu.notificationSettings.alarmToggle'
                })}
              </div>
              <div className="footNote name" data-sort-by="name">
                {formatMessage({
                  defaultMessage: 'Alarm',
                  id: 'menu.notificationSettings.alarmLabel'
                })}
              </div>
              <div className="footNote description">
                {formatMessage({
                  defaultMessage: 'Trigger description',
                  id: 'menu.notificationSettings.alarmDescription'
                })}
              </div>
              <div className="footNote settings">
                {formatMessage({
                  defaultMessage: 'Trigger settings',
                  id: 'menu.notificationSettings.alarmSettings'
                })}
              </div>
              {notificationsMailingPermission && (
                <div className="footNote subscription">
                  {formatMessage({
                    defaultMessage: 'Subscription',
                    id: 'menu.notificationSettings.alarmSubscription'
                  })}
                </div>
              )}
            </div>
            <div className="userNotifications__alarmsItems">
              {alarmsCatalog?.map(alarm => (
                <AlarmItem
                  alarm={alarm}
                  alarms={alarms}
                  key={alarm.type}
                  notificationsMailingPermission={notificationsMailingPermission}
                  onAddAlarm={handleAddAlarm}
                  onSetAlarm={handleSetAlarm}
                  user={user}
                  userRoles={userRoles}
                />
              ))}
            </div>
          </div>
          {notificationsMailingPermission && (
            <div className="userNotifications__alarmSubscriptionSettings">
              <label htmlFor="alarmSubscriptionInput">
                {`${formatMessage({
                  defaultMessage: 'Subscription Emails',
                  id: 'menu.notificationSettings.subscriptionLabel'
                })}:`}
              </label>
              <Input
                className="subscriptionEmail"
                defaultValue={
                  currentNotifications.email ? currentNotifications.email.split(',').join(', ') || null : null
                }
                id="alarmSubscriptionInput"
                name="subscriptionEmail"
                notFloatingPlaceholder
                onChange={(ev: any) => setEmail(ev.target.value as string)}
                placeholder={formatMessage({
                  defaultMessage: 'Notification emails addresses',
                  id: 'menu.notificationSettings.subscriptionEmailsPlaceHolder'
                })}
                shape="square"
                type="text"
              />
            </div>
          )}
        </div>
      </Modal>
    </NotificationSettingsStyled>
  );
}

export default NotificationSettings;
